import { Link } from '@lifeomic/chroma-react/components/Link';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { SpecificityIndicator } from '../Indicators';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { useDrugsData } from './useDrugsData';
import React from 'react';
import EmptyPageState from '@lifeomic/phc-web-toolkit/src/components/EmptyPageState';
import { DNAIcon } from '../../../../src/components/common/icons';
import urls from '../../../../src/util/urls';

const messages = defineMessages({
  noDrugs: {
    id: 'omicsExplorer.variantDetails.dialog.noDrugs',
    defaultMessage: 'No drug associations found',
  },
});

const getReferenceGeneFromType = ({ variant, variantType }) => {
  if (variantType === 'short') {
    return (
      (variant.ensemblCanon &&
        variant.ensemblCanon[0] &&
        variant.ensemblCanon[0].gene) ||
      (variant.ensembl && variant.ensembl[0] && variant.ensembl[0].gene)
    );
  }

  if (variantType === 'structural') {
    const { geneId1, geneId2 } = variant;

    return geneId1 || geneId2;
  }

  if (variantType === 'copyNumber') {
    return variant.geneId;
  }

  if (variantType === 'expression') {
    return variant.gene;
  }

  return null;
};

/**
 * Determines if the cell content should be wrapped in a tooltip or not based
 * on the length of the content.  Since we rely on CSS to text-overflow
 * ellipsis long `td` content, this length is mostly hand-curated.
 */
const maybeRenderTooltipContent = ({ cell, valuePath, length = 21 }) => {
  if (!cell || !valuePath) {
    return null;
  }

  const content = cell[valuePath];

  if (content && content.length > length) {
    return (
      <Tooltip title={content} placement="bottom">
        {/* Tooltip needs an element that can hold a ref */}
        <span>{content}</span>
      </Tooltip>
    );
  }

  return content;
};

const getTableConfig = ({ isAllByGene, isAllByGeneAndFeature, onSort }) => [
  {
    header: {
      label: (
        <FormattedMessage
          id="omicsExplorer.variantDetails.dialog.drugsTable.ckb"
          defaultMessage="CKB"
        />
      ),
      onSort: ({ sortDirection }) => {
        onSort({
          keys: ['byGeneAndFeature'],
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => {
        // Quick escape checks on which SpecificityIndicator to display
        if (isAllByGene) {
          return <SpecificityIndicator match={'gene'} />;
        }

        if (isAllByGeneAndFeature) {
          return <SpecificityIndicator match={'variant'} />;
        }

        // We have a mixture of "byGene" and "byGeneAndFeature" drugs,
        // so we need to check which one to display
        const { byGeneAndFeature } = value;

        return (
          <SpecificityIndicator match={byGeneAndFeature ? 'variant' : 'gene'} />
        );
      },
    },
  },
  {
    header: {
      label: (
        <FormattedMessage
          id="omicsExplorer.variantDetails.dialog.drugsTable.gene"
          defaultMessage="Gene"
        />
      ),
    },
    cell: {
      content: cell => maybeRenderTooltipContent({ cell, valuePath: 'gene' }),
    },
  },
  {
    header: {
      label: (
        <FormattedMessage
          id="omicsExplorer.variantDetails.dialog.drugsTable.feature"
          defaultMessage="Feature"
        />
      ),
      onSort: ({ sortDirection }) => {
        onSort({
          keys: ['feature'],
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: cell =>
        maybeRenderTooltipContent({ cell, valuePath: 'feature' }),
    },
  },
  {
    header: {
      label: (
        <FormattedMessage
          id="omicsExplorer.variantDetails.dialog.drugsTable.source"
          defaultMessage="Source"
        />
      ),
      onSort: ({ sortDirection }) => {
        onSort({
          keys: ['source'],
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: cell => maybeRenderTooltipContent({ cell, valuePath: 'source' }),
    },
  },
  {
    header: {
      label: (
        <FormattedMessage
          id="omicsExplorer.variantDetails.dialog.drugsTable.drug"
          defaultMessage="Drug"
        />
      ),
      onSort: ({ sortDirection }) => {
        onSort({
          keys: ['drug'],
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: cell => maybeRenderTooltipContent({ cell, valuePath: 'drug' }),
    },
  },
  {
    header: {
      label: (
        <FormattedMessage
          id="omicsExplorer.variantDetails.dialog.drugsTable.responseType"
          defaultMessage="Response Type"
        />
      ),
      onSort: ({ sortDirection }) => {
        onSort({
          keys: ['responseType'],
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: cell =>
        maybeRenderTooltipContent({ cell, valuePath: 'responseType' }),
    },
  },
  {
    header: {
      label: (
        <FormattedMessage
          id="omicsExplorer.variantDetails.dialog.drugsTable.evidenceLevel"
          defaultMessage="Evidence Level"
        />
      ),
      onSort: ({ sortDirection }) => {
        onSort({
          keys: ['evidenceLevel'],
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: cell =>
        maybeRenderTooltipContent({ cell, valuePath: 'evidenceLevel' }),
    },
  },
  {
    header: {
      label: (
        <FormattedMessage
          id="omicsExplorer.variantDetails.dialog.drugsTable.sourceEvidence"
          defaultMessage="Source Evidence"
        />
      ),
      onSort: ({ sortDirection }) => {
        onSort({
          keys: ['sourceEvidence'],
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: cell =>
        maybeRenderTooltipContent({ cell, valuePath: 'sourceEvidence' }),
    },
  },
  {
    header: {
      label: (
        <FormattedMessage
          id="omicsExplorer.variantDetails.dialog.drugsTable.disease"
          defaultMessage="Disease"
        />
      ),
      onSort: ({ sortDirection }) => {
        onSort({
          keys: ['disease'],
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: cell =>
        maybeRenderTooltipContent({ cell, valuePath: 'disease' }),
    },
  },
  {
    header: {
      label: (
        <FormattedMessage
          id="omicsExplorer.variantDetails.dialog.drugsTable.publication"
          defaultMessage="Publication"
        />
      ),
    },
    cell: {
      content: value => {
        const { publication } = value;

        if (!publication) {
          return null;
        }

        return (
          <Link to={urls.ncbi.pubmed(publication)} newTab>
            {publication}
          </Link>
        );
      },
    },
  },
  {
    header: {
      label: (
        <FormattedMessage
          id="omicsExplorer.variantDetails.dialog.drugsTable.comment"
          defaultMessage="Comment"
        />
      ),
    },
    cell: {
      content: cell =>
        maybeRenderTooltipContent({ cell, valuePath: 'comment' }),
    },
  },
];

export const DrugAssociationsTable = ({
  contentClassName,
  data,
  variantType,
  emptyStateClassName,
}) => {
  const referenceGene = getReferenceGeneFromType({
    variant: data,
    variantType,
  });
  const { formatMessage } = useIntl();

  const { handleSortClicked, isAllByGene, isAllByGeneAndFeature, sortedData } =
    useDrugsData({ data, referenceGene });

  if (sortedData.length === 0) {
    return (
      <div className={emptyStateClassName}>
        <EmptyPageState
          message={formatMessage(messages.noDrugs)}
          illustration={DNAIcon}
        />
      </div>
    );
  }

  return (
    <div className={contentClassName}>
      <TableModule
        config={getTableConfig({
          isAllByGene,
          isAllByGeneAndFeature,
          onSort: handleSortClicked,
        })}
        data={sortedData}
        maxCellWidth={2}
      />
    </div>
  );
};
